/*
  POLL BLOCK
  ----------------------------------------------------------------------------------------------------------------------
  Behaviour for <poll-block>, rendered by snippets/poll.liquid.

  A poll is answered once. On the first click the bars fill to the percentages the merchant set in the theme editor, the
  chosen option is marked, and the payoff line underneath is revealed. The answer is remembered per browser so coming
  back to the page shows the result straight away rather than asking again.

  The percentages are fixed values from the theme editor, not a live tally -- there is no backend here. Whatever the
  poll is labelled with is the merchant's call; see the note in snippets/poll.liquid.
*/

class PollBlock extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('.poll__option'));
    if (this.options.length === 0) return;

    this.resultElement = this.querySelector('.poll__result');
    this.voted = false;

    const remembered = this.readVote();

    if (remembered === null) {
      this.options.forEach((option, index) => {
        option.addEventListener('click', () => this.vote(index));
      });
    } else {
      // Restoring a previous answer: no transition, the result is simply already there.
      this.reveal(remembered, false);
    }
  }

  get storageKey() {
    return `oscarblue:poll:${this.getAttribute('data-poll-id')}`;
  }

  /*
    Storage can throw outright rather than just come back empty -- Safari in private mode, or a browser set to block
    site data -- so both sides are guarded and a failure simply means the poll asks again.
  */
  readVote() {
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      if (stored === null) return null;

      const index = parseInt(stored, 10);
      return Number.isInteger(index) && index >= 0 && index < this.options.length ? index : null;
    } catch (error) {
      return null;
    }
  }

  writeVote(index) {
    try {
      window.localStorage.setItem(this.storageKey, String(index));
    } catch (error) {
      // Not being able to remember the answer is not a reason to refuse it.
    }
  }

  vote(index) {
    if (this.voted) return;
    this.writeVote(index);
    this.reveal(index, true);
  }

  reveal(chosenIndex, animate) {
    this.voted = true;
    this.classList.add('is-voted');

    this.options.forEach((option, index) => {
      const chosen = index === chosenIndex;
      option.setAttribute('aria-pressed', chosen ? 'true' : 'false');
      /*
        aria-disabled rather than the disabled property: the options stay focusable so a keyboard or screen reader user
        can still read back every result, but activating one no longer does anything.
      */
      option.setAttribute('aria-disabled', 'true');
      option.classList.toggle('is-chosen', chosen);

      const fill = option.querySelector('.poll__fill');
      if (!fill) return;

      const percent = `${option.getAttribute('data-percent') || 0}%`;

      if (animate) {
        // Next frame, so the browser has a zero width to transition away from.
        requestAnimationFrame(() => {
          fill.style.width = percent;
        });
      } else {
        fill.style.width = percent;
      }
    });

    if (this.resultElement) this.resultElement.hidden = false;
  }
}

if (!window.customElements.get('poll-block')) {
  window.customElements.define('poll-block', PollBlock);
}
